/**
 * Export PDF "Scheda prodotto" di un singolo materiale.
 *
 * Una pagina A4 con:
 *  - Header con logo org (riusa drawHeader)
 *  - Box identificativo: nome, codice, fornitore, EAN, descrizione
 *  - Box dimensioni & caratteristiche tecniche
 *  - Box performance certificate (fire, acoustic, thermal, weight)
 *  - Box pricing: netto, listino, % sconto, breakdown
 *  - Box storico prezzi (ultimi 5 cambi) se disponibile
 *  - Footer org
 *
 * Target: condividerla col cliente per giustificare la scelta del prodotto,
 * o salvarla per archivio interno.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { DatabaseMaterial } from '@/hooks/useMaterials';
import type { OrgProfile } from '@/hooks/useOrgSettings';
import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';
import {
  drawHeader, drawFooterOnAllPages, loadImageDataUrl, PRIMARY_RGB, SECONDARY_RGB,
} from './pdfHelpers';
import { formatNumber } from '@/utils/format';

function euro(v: number | null | undefined, decimals = 2): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `€ ${formatNumber(v, decimals)}`;
}

function pct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${formatNumber(v)}%`;
}

function safeStr(v: unknown, fallback = '—'): string {
  if (v == null || v === '') return fallback;
  return String(v);
}

const CATEGORY_LABEL: Record<string, string> = {
  board: 'Lastra',
  ceiling_tile: 'Pannello controsoffitto',
  structure_frame: 'Montante',
  structure_guide: 'Guida',
  insulation: 'Isolante',
  accessory: 'Accessorio',
  screw: 'Vite',
  finish: 'Finitura',
  other: 'Altro',
};

/** Costruisce nome file safe: "Codice — Nome.pdf" senza caratteri proibiti. */
function buildFilename(m: DatabaseMaterial): string {
  const raw = `${m.code} — ${m.name}`.replace(/[\\/:*?"<>|]/g, '_').slice(0, 100);
  return `${raw}.pdf`;
}

interface PriceHistoryRow {
  list_price: number | null;
  unit_price: number | null;
  changed_at: string;
}

async function fetchPriceHistory(materialId: string): Promise<PriceHistoryRow[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('material_price_history')
    .select('list_price, unit_price, changed_at')
    .eq('material_id', materialId)
    .order('changed_at', { ascending: false })
    .limit(5);
  if (error) return [];
  return (data ?? []) as PriceHistoryRow[];
}

export interface ExportMaterialPdfOptions {
  material: DatabaseMaterial;
  org: OrgProfile | null;
}

export async function exportMaterialPdf({ material: m, org }: ExportMaterialPdfOptions): Promise<void> {
  Sentry.addBreadcrumb({
    category: 'pdf.material',
    message: 'start',
    level: 'info',
    data: { materialId: m.id, code: m.code },
  });

  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  const logoDataUrl = await loadImageDataUrl(org?.logo_url);
  const history = await fetchPriceHistory(m.id);

  let y = drawHeader({ pdf, pageWidth, margin, org, logoDataUrl });

  // ============================================================================
  // Titolo
  // ============================================================================
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(...PRIMARY_RGB);
  pdf.text('Scheda prodotto', margin, y + 10);
  pdf.setTextColor(0);
  y += 14;

  // ============================================================================
  // Box identificativo
  // ============================================================================
  pdf.setFillColor(248, 250, 252);
  pdf.rect(margin, y, contentWidth, 28, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(safeStr(m.name), margin + 4, y + 7);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100);
  const headLine1: string[] = [
    `Codice: ${safeStr(m.code)}`,
    `Categoria: ${CATEGORY_LABEL[m.category] ?? m.category}`,
    `Fornitore: ${safeStr(m.supplier)}`,
  ];
  pdf.text(headLine1.join('   ·   '), margin + 4, y + 14);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ean = (m as any).ean_code;
  if (ean) {
    pdf.text(`EAN: ${ean}`, margin + 4, y + 20);
  }
  if (m.description) {
    const wrap = pdf.splitTextToSize(m.description, contentWidth - 8);
    pdf.text(wrap.slice(0, 1), margin + 4, y + (ean ? 25 : 20));
  }
  pdf.setTextColor(0);
  y += 32;

  // ============================================================================
  // Dimensioni & specifiche
  // ============================================================================
  const dimRows: [string, string][] = [];
  if (m.thickness) dimRows.push(['Spessore', `${m.thickness} mm`]);
  if (m.width) dimRows.push(['Larghezza', `${m.width} mm`]);
  if (m.length) dimRows.push(['Lunghezza', `${m.length} mm`]);
  if (m.sheet_thickness) dimRows.push(['Spessore lamiera', `${m.sheet_thickness} mm`]);
  if (m.density) dimRows.push(['Densità', `${m.density} kg/m³`]);
  if (m.box_pieces) dimRows.push(['Pezzi/scatola', String(m.box_pieces)]);
  if (m.unit) dimRows.push(['Unità di misura', m.unit]);
  if (m.incidence_per_sqm) dimRows.push(['Incidenza/m²', `${formatNumber(m.incidence_per_sqm, 3)} ${m.unit}`]);
  if (m.material_type) dimRows.push(['Tipologia', m.material_type]);
  if (m.board_type) dimRows.push(['Tipo lastra', m.board_type]);
  if (m.en_520_type) dimRows.push(['EN 520', m.en_520_type]);
  if (m.profile_type) dimRows.push(['Tipo profilo', m.profile_type]);
  if (m.humidity_resistance_class) dimRows.push(['Resistenza umidità', m.humidity_resistance_class]);

  if (dimRows.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text('Dimensioni & specifiche', margin, y + 4);

    autoTable(pdf, {
      startY: y + 6,
      head: [],
      body: dimRows,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 1.5 },
      columnStyles: { 0: { cellWidth: 60, textColor: [100, 100, 100] }, 1: { fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (pdf as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 40;
    y += 4;
  }

  // ============================================================================
  // Performance certificate
  // ============================================================================
  const perfRows: [string, string][] = [];
  if (m.fire_class) perfRows.push(['Reazione al fuoco', m.fire_class]);
  if (m.fire_resistance_class) perfRows.push(['Resistenza al fuoco', m.fire_resistance_class]);
  if (m.acoustic_performance) perfRows.push(['Rw / acustica', `${m.acoustic_performance} dB`]);
  if (m.thermal_conductivity) perfRows.push(['Conducibilità λ', `${m.thermal_conductivity} W/mK`]);
  if (m.weight_per_sqm) perfRows.push(['Massa areica', `${m.weight_per_sqm} kg/m²`]);
  if (m.weight_per_ml) perfRows.push(['Massa lineare', `${m.weight_per_ml} kg/ml`]);
  if (m.flexural_strength) perfRows.push(['Resistenza flessione', m.flexural_strength]);
  if (m.surface_hardness) perfRows.push(['Durezza superficie', m.surface_hardness]);
  if (m.voc_class) perfRows.push(['VOC', m.voc_class]);
  if (m.recycled_content != null) perfRows.push(['Contenuto riciclato', `${m.recycled_content}%`]);

  if (perfRows.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...PRIMARY_RGB);
    pdf.text('Caratteristiche tecniche', margin, y + 4);
    pdf.setTextColor(0);

    autoTable(pdf, {
      startY: y + 6,
      head: [],
      body: perfRows,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 70, textColor: [100, 100, 100] }, 1: { fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (pdf as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 60;
    y += 4;
  }

  // ============================================================================
  // Pricing
  // ============================================================================
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...PRIMARY_RGB);
  pdf.text('Prezzo', margin, y + 4);
  pdf.setTextColor(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mx = m as any;
  const netPrice = (mx.net_price ?? m.unit_price ?? m.list_price ?? null) as number | null;
  const listPrice = (m.list_price ?? null) as number | null;
  const totalPct = (mx.total_discount_pct ?? null) as number | null;
  const familyPct = (mx.family_discount_pct ?? null) as number | null;
  const extraPct = m.extra_discount ?? null;

  const priceRows: [string, string][] = [
    ['Prezzo netto (con sconti)', `${euro(netPrice, 4)} / ${m.unit}`],
  ];
  if (listPrice != null && netPrice != null && listPrice > netPrice + 0.001) {
    priceRows.push(['Listino fornitore', `${euro(listPrice, 4)} / ${m.unit}`]);
  }
  if (totalPct != null && totalPct > 0.01) {
    priceRows.push(['Sconto totale applicato', pct(totalPct)]);
  }
  if (familyPct != null && familyPct > 0.01) {
    priceRows.push(['  • di cui sconto famiglia', pct(familyPct)]);
  }
  if (extraPct != null && extraPct > 0.01) {
    priceRows.push(['  • di cui extra prodotto', pct(extraPct)]);
  }

  autoTable(pdf, {
    startY: y + 6,
    head: [],
    body: priceRows,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: { 0: { cellWidth: 80, textColor: [100, 100, 100] }, 1: { fontStyle: 'bold', halign: 'right' } },
    margin: { left: margin, right: margin },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (pdf as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 30;
  y += 4;

  // ============================================================================
  // Storico prezzi (ultimi 5 cambi)
  // ============================================================================
  if (history.length > 1 && y < pageHeight - 50) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...SECONDARY_RGB);
    pdf.text('Storico prezzi (ultimi 5 cambi)', margin, y + 4);
    pdf.setTextColor(0);

    autoTable(pdf, {
      startY: y + 6,
      head: [['Data', 'Listino', 'Netto']],
      body: history.map((h) => [
        new Date(h.changed_at).toLocaleDateString('it-IT'),
        euro(h.list_price, 4),
        euro(h.unit_price, 4),
      ]),
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 1.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 0 },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
      margin: { left: margin, right: margin },
    });
  }

  drawFooterOnAllPages({ pdf, pageWidth, pageHeight, org });

  Sentry.addBreadcrumb({ category: 'pdf.material', message: 'save', level: 'info' });
  pdf.save(buildFilename(m));
}
